import {
    Dispatch,
    SetStateAction,
    useState,
    useEffect,
} from 'react';
import '../App.css';
import {
    Modal,
    Button,
    ListGroup,
    Accordion,
    AccordionHeader,
    AccordionBody,
    ModalBody,
    ListGroupItem,
} from 'react-bootstrap';
import CustomTooltip from './CustomTooltip';
import { FcInfo } from "react-icons/fc";
import Season from '../schemas/Season';
import Rankings from '../schemas/Rankings';
import VeteransDraft, { VeteranPick } from '../schemas/VeteranDraft';
import { adjustTeamName } from '../utils';


interface ModalProps {
    show: boolean;
    setShow: Dispatch<SetStateAction<boolean>>;
    fullscreen: boolean;
    clickedSeason: Season;
    seasonVetDraft: VeteransDraft;
}

function SeasonModal({ show, setShow, clickedSeason, seasonVetDraft }: ModalProps) {
    const [movesIndex, setMovesIndex] = useState("");
    const [movesLimit, setMovesLimit] = useState(0);

    useEffect(() => {
        if (show) {
            clickedSeason.finalRanks.sort((a: Rankings, b: Rankings) => a.rank - b.rank);

            const seasonalGames = clickedSeason.gamesPlayed ? clickedSeason.gamesPlayed : 82;
            const totalTeams = clickedSeason.totalTeams ? clickedSeason.totalTeams : 11;
            const normalizedMoves = (clickedSeason.totalMoves) / (seasonalGames * 15 * totalTeams);
            setMovesIndex(normalizedMoves.toFixed(2));
            if (clickedSeason.year === '2013/14') {
                setMovesLimit(380);
            } else {
                clickedSeason.movesLimit ? setMovesLimit(200) : setMovesLimit(0);
            }
        };

    }, [show, clickedSeason, seasonVetDraft]);

    function handlePickColor(pick: VeteranPick) {
        let textColor: string = '';
        if (pick.amongBestPicks) {
            textColor = 'text-success';
        }
        if (pick.amongWorstPicks) {
            textColor = 'text-danger';
        }
        return textColor;
    }

    return (
        <Modal show={show} fullscreen dir='rtl'>
            <div className='season-modal-header'>
                <Modal.Title
                    className='season-modal-title mt-2 me-3 fs-2 text-center'
                >
                    עונת {clickedSeason.year}
                    <Button
                    variant="light"
                    aria-label="Close"
                    onClick={() => setShow(false)}
                    className='m-3'
                    style={{ float: 'left'}}
                >
                    סגירה
                </Button>
                </Modal.Title>
                
            </div>
            <ModalBody className='d-flex'>
                <div className='w-50'>
                    <h5 className='me-3 mt-3 text-decoration-underline'>
                        נתונים כלליים
                    </h5>
                    <ListGroup variant="flush">
                        {clickedSeason.totalTeams && (
                            <ListGroupItem>
                                <span>
                                    מספר המשתתפים:&nbsp;
                                </span>
                                <span>
                                    {clickedSeason.totalTeams}
                                </span>
                            </ListGroupItem>
                        )}
                        <ListGroupItem>
                            <span>
                                כמות משחקים לקבוצת אן בי איי:&nbsp;
                            </span>
                            <span>
                                {
                                    clickedSeason.gamesPlayed ?
                                        clickedSeason.gamesPlayed : '82'
                                }
                            </span>
                        </ListGroupItem>
                        <ListGroupItem>
                            <span>
                                סה"כ מהלכים של כל הקבוצות:&nbsp;
                            </span>
                            <span>
                                {clickedSeason.totalMoves}
                            </span>
                        </ListGroupItem>
                        <ListGroupItem className='d-flex'>

                            <span>
                                מדד המהלכים העונתי:&nbsp;
                            </span>
                            <span>
                                {movesIndex}&nbsp;
                            </span>
                            <CustomTooltip
                                title='סך כל המהלכים חלקי: מכפלת 11 או 13 (כמות הקבוצות בליגה) בכמות משחקי האן בי איי ששוחקו'
                            >
                                <span>
                                    <FcInfo className='me-2' />
                                </span>
                            </CustomTooltip>
                        </ListGroupItem>
                        <ListGroupItem>
                            <span>
                                מכסת מהלכים לקבוצה:&nbsp;
                            </span>
                            <span>
                                {movesLimit > 0 ? movesLimit : "בלתי מוגבלת"}
                            </span>
                        </ListGroupItem>
                        <CustomTooltip
                            title={
                                clickedSeason.injurySlot ? 'משבצת פצועים' : 'ללא משבצת פצועים'
                            }
                        >
                            {
                                clickedSeason.injurySlot ? (
                                    <span
                                        className='il-slot border rounded text-center fw-bold mt-2'
                                    >
                                        {clickedSeason.ILplus ? '+IL' : 'IL'}
                                    </span>
                                ) : (
                                    <img
                                        src='../no-il.png'
                                        style={{ height: '25px', width: '45px' }}
                                        className='mt-2 me-3'
                                    />
                                )
                            }
                        </CustomTooltip>
                        <ListGroupItem>
                            <span>
                                דירוג עונתי:&nbsp;
                            </span>
                            {[...clickedSeason.finalRanks].map((team, idx) => (
                                <ListGroupItem key={idx} dir='ltr'>
                                    <span>
                                        {team.rank}.&nbsp;
                                    </span>
                                    <span>
                                        {adjustTeamName(team.teamName, clickedSeason.year)}
                                    </span>
                                </ListGroupItem>

                            ))}
                        </ListGroupItem>
                    </ListGroup>
                </div>
                <div className='w-50'>
                    <h5 className='mt-3 text-decoration-underline'>
                        קיפרים ובחירות דראפט
                    </h5>
                    <ListGroup>
                        <ListGroupItem>
                            <span>
                                שחקני פרנצ'ייז מנדטוריים:&nbsp;
                            </span>
                            <span>
                                {
                                    clickedSeason.franchisePlayersRequired ?
                                        clickedSeason.franchisePlayersRequired : "אחד"
                                }
                            </span>
                        </ListGroupItem>
                        {
                            clickedSeason.vetDraftTopPick && (
                                <ListGroupItem>
                                    <span>
                                        בחר ראשון בדראפט וטרנים:&nbsp;
                                    </span>
                                    <span>
                                        מקום&nbsp;
                                        {clickedSeason.vetDraftTopPick}&nbsp;
                                        בעונה הקודמת
                                    </span>
                                </ListGroupItem>
                            )
                        }
                        {
                            seasonVetDraft.picks.length > 0 && (
                                <Accordion>
                                    <AccordionHeader>
                                        <span className='ms-3'>
                                            דראפט וטרנים&nbsp;
                                        </span>
                                        <span className='green-legend border border-success ms-2'>
                                            ח</span>
                                        <span className='legend ms-2'>
                                            בחירה טובה במיוחד
                                        </span>
                                        <span className='red-legend border border-danger ms-2'>
                                            ח

                                        </span>
                                        <span className='legend'>
                                            בחירה רעה במיוחד&nbsp;
                                        </span>
                                    </AccordionHeader>
                                    <AccordionBody>
                                        {
                                            seasonVetDraft.picks.map((pick, idx) => (
                                                <ListGroup key={idx} dir='ltr'>
                                                    <ListGroupItem>
                                                        <span>
                                                            {idx + 1}.&nbsp;
                                                        </span>
                                                        <span>
                                                            {pick.team}:&nbsp;
                                                        </span>
                                                        <span
                                                            className={
                                                                pick.amongBestPicks ||
                                                                    pick.amongWorstPicks ?
                                                                    handlePickColor(pick) : ''
                                                            }
                                                        >
                                                            {pick.player}
                                                        </span>
                                                    </ListGroupItem>
                                                </ListGroup>

                                            ))
                                        }

                                    </AccordionBody>
                                </Accordion>
                            )
                        }
                        <Accordion>
                            <AccordionHeader >
                                <span className='ms-3'>
                                    דראפט סנייק (שני סיבובים ראשונים בלבד)&nbsp;
                                </span>
                                <span className='green-legend border border-success ms-2'>
                                    ח</span>
                                <span className='legend ms-2'>
                                    בחירה טובה במיוחד
                                </span>
                                <span className='red-legend border border-danger ms-2'>
                                    ח

                                </span>
                                <span className='legend'>
                                    בחירה רעה במיוחד&nbsp;
                                </span>

                            </AccordionHeader>
                            <AccordionBody>
                                {
                                    clickedSeason.draftRounds.map((el, idx) => (
                                        <ListGroup key={idx}>
                                            <ListGroupItem >
                                                <span>
                                                    {el.draftPick.pickNumber}.&nbsp;
                                                    {el.draftPick.draftingManager}: &nbsp;
                                                </span>
                                                {
                                                    el.draftPick.becameKeeper ? (
                                                        <span
                                                            style={
                                                                {
                                                                    fontSize: "10px",
                                                                    fontWeight: "bolder",
                                                                    border: "1px solid black",
                                                                    padding: "2px",
                                                                    marginRight: "7px"
                                                                }
                                                            }
                                                        >
                                                            K
                                                        </span>
                                                    ) : ''
                                                }
                                                {
                                                    el.draftPick.wasSleeper ? (
                                                        <span
                                                            className='fst-italic border border-danger bg-danger text-white rounded-pill'>
                                                            &nbsp;Sleeper&nbsp;
                                                        </span>
                                                    ) : ''
                                                }
                                                <span
                                                    className={
                                                        el.draftPick.amongBestPicks ||
                                                            el.draftPick.amongWorstPicks ?
                                                            handlePickColor(el.draftPick) : ''
                                                    }
                                                >
                                                    &nbsp;{el.draftPick.draftedPlayer}
                                                </span>

                                            </ListGroupItem>
                                        </ListGroup>
                                    ))
                                }
                            </AccordionBody>
                        </Accordion>

                    </ListGroup>

                </div>
            </ModalBody>


        </Modal>
    )
}

export default SeasonModal;